import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Camera, Image as ImageIcon, CheckCircle2, AlertTriangle, Upload, Loader2, RefreshCw } from "lucide-react";
import { compressImage } from "@/lib/imageCompression";
import SwissBadge from "@/components/SwissBadge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/public/photo-bridge`;

/**
 * MobileUploadPage — Public-Seite (kein Login). User landet hier durch
 * QR-Scan vom Desktop-Bridge-Modal. Erlaubt direktes Fotografieren ODER
 * Galerie-Auswahl ODER beides gleichzeitig.
 *
 * Route: `/u/:token`
 */
export default function MobileUploadPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);       // { case_reference, expires_at, ... }
  const [loadError, setLoadError] = useState(null);
  const [uploads, setUploads] = useState([]);   // [{ name, status, error }]
  const [busy, setBusy] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // 1) Beim Mount: Token validieren
  useEffect(() => {
    if (!token) return;
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/${token}`);
        if (active) setInfo(data);
      } catch (e) {
        if (!active) return;
        const status = e?.response?.status;
        if (status === 404) setLoadError("Link nicht gefunden");
        else if (status === 410) setLoadError("Link ist abgelaufen oder wurde widerrufen");
        else if (status === 429) setLoadError("Maximale Anzahl Uploads erreicht");
        else setLoadError("Verbindungsfehler");
      }
    })();
    return () => { active = false; };
  }, [token]);

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const files = Array.from(fileList);
    setBusy(true);

    // Optimistic UI: zeigen alle als "pending"
    const baseIdx = uploads.length;
    setUploads((prev) => [
      ...prev,
      ...files.map((f) => ({ name: f.name || "Foto", status: "pending" })),
    ]);

    const setStatus = (i, patch) => {
      setUploads((prev) => {
        const next = [...prev];
        next[baseIdx + i] = { ...next[baseIdx + i], ...patch };
        return next;
      });
    };

    // Client-seitig komprimieren (5–12 MB → ~300 KB) und 3 parallel hochladen.
    // Vorher liefen unkomprimierte Fotos einzeln nacheinander → viel zu langsam.
    let cursor = 0;
    const worker = async () => {
      while (cursor < files.length) {
        const i = cursor++;
        try {
          const compressed = await compressImage(files[i]);
          const fd = new FormData();
          fd.append("files", compressed, compressed.name || files[i].name || "foto.jpg");
          await axios.post(`${API}/${token}/upload`, fd, { timeout: 90_000 });
          setStatus(i, { status: "done" });
        } catch (e) {
          const msg =
            e?.response?.status === 410 ? "Link abgelaufen" :
            e?.response?.status === 413 ? "Datei zu groß" :
            e?.response?.status === 429 ? "Limit erreicht" :
            "Upload fehlgeschlagen";
          setStatus(i, { status: "error", error: msg });
        }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, files.length) }, worker));
    setBusy(false);
  };

  // ───────── Render-States ─────────
  if (loadError) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-rose-50 to-zinc-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-2xl shadow-lg max-w-md w-full p-8 text-center">
          <div className="h-14 w-14 mx-auto rounded-full bg-rose-100 flex items-center justify-center mb-4">
            <AlertTriangle className="h-7 w-7 text-rose-600" />
          </div>
          <h1 className="text-xl font-display font-semibold text-zinc-900 mb-2">
            Link nicht verfügbar
          </h1>
          <p className="text-sm text-zinc-600">{loadError}</p>
          <p className="text-xs text-zinc-400 mt-4">
            Bitte den Gutachter um einen neuen QR-Code bitten.
          </p>
        </div>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 text-zinc-400 animate-spin" />
      </div>
    );
  }

  const successCount = uploads.filter((u) => u.status === "done").length;
  const remaining = (info.max_uploads || 100) - (info.upload_count || 0) - successCount;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-zinc-50 to-teal-50 pb-32">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-md mx-auto px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <Camera className="h-5 w-5 text-white" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-zinc-900 truncate">
                Akte {info.case_reference}
              </div>
              <div className="text-xs text-zinc-500">
                Fotos für die Akte hochladen
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Action-Buttons */}
      <main className="max-w-md mx-auto px-5 pt-6 space-y-3">
        <button
          onClick={() => cameraInputRef.current?.click()}
          disabled={busy || remaining <= 0}
          className="w-full flex items-center gap-4 p-5 bg-white rounded-2xl shadow-sm border border-zinc-200 hover:border-emerald-300 active:scale-[0.99] transition disabled:opacity-50"
          data-testid="mobile-camera-btn"
        >
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shrink-0">
            <Camera className="h-6 w-6 text-white" strokeWidth={1.8} />
          </div>
          <div className="text-left flex-1">
            <div className="font-semibold text-zinc-900">Foto aufnehmen</div>
            <div className="text-xs text-zinc-500">Kamera öffnen und direkt fotografieren</div>
          </div>
        </button>

        <button
          onClick={() => galleryInputRef.current?.click()}
          disabled={busy || remaining <= 0}
          className="w-full flex items-center gap-4 p-5 bg-white rounded-2xl shadow-sm border border-zinc-200 hover:border-emerald-300 active:scale-[0.99] transition disabled:opacity-50"
          data-testid="mobile-gallery-btn"
        >
          <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center shrink-0">
            <ImageIcon className="h-6 w-6 text-white" strokeWidth={1.8} />
          </div>
          <div className="text-left flex-1">
            <div className="font-semibold text-zinc-900">Aus Galerie wählen</div>
            <div className="text-xs text-zinc-500">Bereits aufgenommene Fotos auswählen</div>
          </div>
        </button>

        {/* Hidden inputs */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid="mobile-camera-input"
        />
        <input
          ref={galleryInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
          data-testid="mobile-gallery-input"
        />

        {/* Status */}
        {uploads.length > 0 && (
          <div className="mt-6 bg-white rounded-2xl shadow-sm border border-zinc-200 p-4" data-testid="mobile-upload-list">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-semibold text-zinc-900">
                {successCount} von {uploads.length} hochgeladen
              </div>
              {busy && <Loader2 className="h-4 w-4 text-emerald-600 animate-spin" />}
            </div>
            <ul className="space-y-1.5 max-h-[40vh] overflow-y-auto">
              {uploads.map((u, i) => (
                <li
                  key={i}
                  className="flex items-center gap-2.5 text-xs"
                  data-testid={`mobile-upload-item-${i}`}
                >
                  {u.status === "done" ? (
                    <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" />
                  ) : u.status === "error" ? (
                    <AlertTriangle className="h-4 w-4 text-rose-600 shrink-0" />
                  ) : (
                    <Loader2 className="h-4 w-4 text-zinc-400 animate-spin shrink-0" />
                  )}
                  <span className="truncate flex-1 text-zinc-700">{u.name}</span>
                  {u.status === "error" && (
                    <span className="text-rose-600 text-[10px] shrink-0">{u.error}</span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Hint */}
        {successCount > 0 && (
          <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-2xl p-4 text-center" data-testid="mobile-success-banner">
            <CheckCircle2 className="h-8 w-8 text-emerald-600 mx-auto mb-2" />
            <div className="text-sm font-semibold text-emerald-900 mb-1">
              Erfolgreich übertragen
            </div>
            <div className="text-xs text-emerald-700">
              Die Fotos sind jetzt am Desktop in der Akte sichtbar. Du kannst weitere Fotos hinzufügen.
            </div>
          </div>
        )}

        {remaining <= 0 && (
          <div className="mt-6 bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
            <AlertTriangle className="h-6 w-6 text-amber-600 mx-auto mb-2" />
            <div className="text-sm font-semibold text-amber-900">Limit erreicht</div>
            <div className="text-xs text-amber-700 mt-1">
              Maximale Anzahl Uploads für diesen Link erreicht. Bitte den Gutachter um einen neuen Link.
            </div>
          </div>
        )}

        <div className="text-center text-[11px] text-zinc-400 pt-4">
          <Upload className="h-3 w-3 inline mr-1" />
          Sicherer Direkt-Upload · Easy Gutachter
        </div>
        <div className="flex justify-center pt-3 pb-6">
          <SwissBadge />
        </div>
      </main>
    </div>
  );
}
